import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBookOpen, 
  faLeaf, 
  faNewspaper, 
  faCalendarAlt,
  faUserTie,
  faCamera,
  faPenFancy,
  faCoffee,
  faArrowRight,
  faSpinner,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { theme } from '../../../styles/theme';
import blogService from '../../../services/blogService';
import BlogPostDetail from './BlogPostDetail';

// Import images from existing assets

import castleImage from '../../../assets/castle2.png';
import playgroundImage from '../../../assets/playground.jpg';


const JournalContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const ComingSoonSection = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 5rem 2rem;
  margin: 5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    left: 0;
    width: 100%;
    height: 100px;
    background-color: white;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 0);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: 0;
    width: 100%;
    height: 100px;
    background-color: white;
    clip-path: polygon(0 0, 100% 100%, 0 100%);
  }
`;

const ComingSoonContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const ComingSoonTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.2rem;
  color: white;
  margin-bottom: 2rem;
`;

const ComingSoonText = styled.p`
  font-size: 1.3rem;
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 50%;
  color: white;
  font-size: 32px;
`;

const PreviewSection = styled.div`
  margin: 5rem 0;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewCard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }
`;

const PreviewContent = styled.div`
  padding: 2rem;
  text-align: center;
`;

const PreviewTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const PreviewDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
`;

// Blog posts styled components
const BlogSection = styled.div`
  margin: 5rem 0;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled.article`
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
  }
`;

const BlogImage = styled.div`
  width: 100%;
  height: 200px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  background-color: ${props => props.theme.colors.greyBackground};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(transparent, rgba(0,0,0,0.3));
  }
`;

const BlogContent = styled.div`
  padding: 2rem;
`;

const BlogMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.7;
`;

const BlogTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  line-height: 1.3;
`;

const BlogExcerpt = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const ReadMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.accent};
  background: none;
  border: none;
  font-weight: 500;
  cursor: pointer;
  font-size: inherit;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: translateX(4px);
  }
`;

const LoadingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.primary};
`;

const LoadingSpinner = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorSection = styled.div`
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  padding: 2rem;
  margin: 2rem 0;
  text-align: center;
  color: #c33;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.7;
`;

const Journal = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { ref: previewRef, inView: previewInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  // State for blog posts
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasPosts, setHasPosts] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  // Fetch blog posts on component mount
  useEffect(() => {
    const fetchPosts = async () => {
      try {

        setLoading(true);
        setError(null);
        const blogPosts = await blogService.getPosts({ per_page: 6 });

        setPosts(blogPosts);
        setHasPosts(blogPosts.length > 0);

      } catch (err) {

        setError('Unable to load blog posts. Please try again later.');
      } finally {
        setLoading(false);

      }
    };

    fetchPosts();
  }, []);

  // Load specific post if postId is in URL
  useEffect(() => {
    const loadPostFromUrl = async () => {
      if (postId && !selectedPost) {
        try {

          setLoading(true);
          const post = await blogService.getPostById(postId);
          if (post) {
            const fullPost = await blogService.getPostWithFullContent(post);
            setSelectedPost(fullPost);
          } else {

            setError('Blog post not found');
          }
        } catch (err) {

          setError('Unable to load blog post');
        } finally {
          setLoading(false);
        }
      }
    };

    loadPostFromUrl();
  }, [postId, selectedPost]);

  // Render blog posts
  const renderBlogPosts = () => {

    if (loading) {

      return (
        <LoadingSection>
          <LoadingSpinner>
            <FontAwesomeIcon icon={faSpinner} />
          </LoadingSpinner>
          <p>Loading blog posts...</p>
        </LoadingSection>
      );
    }

    if (error) {

      return (
        <ErrorSection>
          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginBottom: '1rem', fontSize: '2rem' }} />
          <h3>Unable to Load Blog Posts</h3>
          <p>{error}</p>
        </ErrorSection>
      );
    }

    if (!hasPosts) {

      return (
        <EmptyState>
          <IconWrapper>
            <FontAwesomeIcon icon={faBookOpen} />
          </IconWrapper>
          <h3>No blog posts yet</h3>
          <p>Check back soon for updates from Belvoir Castle!</p>
        </EmptyState>
      );
    }

    return (
      <BlogGrid>
        {posts.map((post) => (
          <BlogCard key={post.id}>
            <BlogImage 
              src={post.featuredImage?.url || castleImage} 
              alt={post.featuredImage?.alt || post.title}
            />
            <BlogContent>
              <BlogMeta>
                <span>
                  <FontAwesomeIcon icon={faCalendarAlt} /> {blogService.formatDate(post.date)}
                </span>
                <span>
                  <FontAwesomeIcon icon={faUserTie} /> {post.author.name || 'Belvoir Castle'}
                </span>
              </BlogMeta>
              <BlogTitle>{post.title}</BlogTitle>
              <BlogExcerpt>
                {post.excerpt || blogService.generateExcerpt(post.content, 120)}
              </BlogExcerpt>
              <ReadMoreButton onClick={() => {
                navigate(`/journal/${post.slug || post.id}`);
              }}>
                Read More <FontAwesomeIcon icon={faArrowRight} />
              </ReadMoreButton>
            </BlogContent>
          </BlogCard>
        ))}
      </BlogGrid>
    );
  };
  
  // If a post is selected, show the detail view
  if (selectedPost) {
    return (
      <BlogPostDetail 
        post={selectedPost} 
        onBack={() => {
          setSelectedPost(null);
          navigate('/journal');
        }} 
      />
    );
  }

  return (
    <JournalContainer>
      <ParallaxBanner 
        title="The Belvoir Journal"
        subtitle="Stories from the Castle"
        image={castleImage}
      />
      
      <ContentContainer>
        <SectionTitle>Welcome to The Belvoir Journal</SectionTitle>
        <IntroText>
          Discover the fascinating world of Belvoir Castle through our journal. 
          From garden stories to historical insights, we bring you the latest news and updates from our 
          beautiful estate.
        </IntroText>
        
        <BlogSection>
          <SectionTitle>Latest Stories</SectionTitle>
          {renderBlogPosts()}
        </BlogSection>
        
        <PreviewSection ref={previewRef}>
          <SectionTitle>What to Expect</SectionTitle>
          <PreviewGrid>
            <PreviewCard>
              <PreviewContent>
                <IconWrapper>
                  <FontAwesomeIcon icon={faLeaf} />
                </IconWrapper>
                <PreviewTitle>Garden Stories</PreviewTitle>
                <PreviewDescription>
                  Insights from our Head Gardener about seasonal changes, plant care, and the 
                  beautiful landscapes of Belvoir Castle.
                </PreviewDescription>
              </PreviewContent>
            </PreviewCard>
            
            <PreviewCard>
              <PreviewContent>
                <IconWrapper>
                  <FontAwesomeIcon icon={faNewspaper} />
                </IconWrapper>
                <PreviewTitle>Castle Updates</PreviewTitle>
                <PreviewDescription>
                  Latest news and developments from around the estate, including restoration 
                  projects and special events.
                </PreviewDescription>
              </PreviewContent>
            </PreviewCard>
            
            <PreviewCard>
              <PreviewContent>
                <IconWrapper>
                  <FontAwesomeIcon icon={faUserTie} />
                </IconWrapper>
                <PreviewTitle>Office Insights</PreviewTitle>
                <PreviewDescription>
                  Behind-the-scenes stories from our office team about what goes into running 
                  a historic estate.
                </PreviewDescription>
              </PreviewContent>
            </PreviewCard>
            
            <PreviewCard>
              <PreviewContent>
                <IconWrapper>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </IconWrapper>
                <PreviewTitle>Event Highlights</PreviewTitle>
                <PreviewDescription>
                  Recaps of special events, celebrations, and memorable moments at Belvoir Castle.
                </PreviewDescription>
              </PreviewContent>
            </PreviewCard>
            
            <PreviewCard>
              <PreviewContent>
                <IconWrapper>
                  <FontAwesomeIcon icon={faCamera} />
                </IconWrapper>
                <PreviewTitle>Photo Stories</PreviewTitle>
                <PreviewDescription>
                  Visual journeys through the changing seasons and special moments at the castle.
                </PreviewDescription>
              </PreviewContent>
            </PreviewCard>
            
            <PreviewCard>
              <PreviewContent>
                <IconWrapper>
                  <FontAwesomeIcon icon={faPenFancy} />
                </IconWrapper>
                <PreviewTitle>Historical Tales</PreviewTitle>
                <PreviewDescription>
                  Fascinating stories from the castle's rich history and heritage.
                </PreviewDescription>
              </PreviewContent>
            </PreviewCard>
          </PreviewGrid>
        </PreviewSection>
      </ContentContainer>
      
      <Footer />
    </JournalContainer>
  );
};

export default Journal; 